import React, { useCallback, useEffect, useRef, useState } from 'react'
import { intl } from 'lang/intl'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useEffectOnce, usePreviousDistinct } from 'react-use'
import { ViewportList, type ViewportListRef } from 'react-viewport-list'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { TtDialog, TtIconButton, TtProgressBar } from '@23/ui-react'

import {
  contrastValue,
  useWebinarColors,
} from '../../../Designs/blocs/useWebinarColors'
import {
  changeCardOrder,
  useActiveTrayCard,
  useActiveTrayCardIndex,
  useTray,
} from '../../../firebase/hooks/useTray'
import useFeatures from '../../../sharedLegacy/hooks/useFeatures'

import { useIsUploadingHandout } from './hooks/useHandout'
import { TrayItem } from './TrayItem'
import { useTrayCardEditingId } from './useTrayEditing'
import { GutterMenu } from './GutterMenu/GutterMenu'
import { TrayFloorTrigger } from './TrayFloorTrigger'

import './Tray.scss'

type Writeable<T> = { -readonly [P in keyof T]: T[P] }

const Tray = () => {
  const { HasWebinarMixesEnabled } = useFeatures()
  const { cardOrder } = useTray()
  const activeTrayCard = useActiveTrayCard()
  const activeTrayCardIdx = useActiveTrayCardIndex()
  const previousActiveTrayCard = usePreviousDistinct(activeTrayCard)
  const currentlyEditing = useTrayCardEditingId()
  const { background } = useWebinarColors()
  const lowConstrastBackground = contrastValue('#ffffff', background.color) < 20

  const ref = useRef<HTMLDivElement | null>(null)
  const cardListRef = useRef<ViewportListRef>(null)

  const [isSorting, setIsSorting] = useState(false)
  const [isTrayOpen, setIsTrayOpen] = useState(true)
  const [isSmoothScrollingLocked, setIsSmoothScrollingLocked] = useState(true)

  const sensors = useSensors(useSensor(PointerSensor))

  const isUploadingHandout = useIsUploadingHandout()

  useEffectOnce(() => {
    setTimeout(() => setIsSmoothScrollingLocked(false), 3000)
  })

  useEffect(() => {
    if (
      activeTrayCardIdx !== -1 &&
      previousActiveTrayCard !== activeTrayCard &&
      !currentlyEditing
    ) {
      cardListRef.current?.scrollToIndex({
        index: activeTrayCardIdx,
        alignToTop: true,
        offset: -(window.innerHeight * 0.3),
        prerender: 8,
        delay: 100,
      })
    }
  }, [
    activeTrayCard,
    activeTrayCardIdx,
    currentlyEditing,
    previousActiveTrayCard,
  ])

  const onTrayCloseClickHandler = useCallback(() => {
    setIsTrayOpen(false)
  }, [])

  const onTrayOpenClickHandler = useCallback(() => {
    setIsTrayOpen(true)
  }, [])

  const onDragEndHandler = (event: DragEndEvent) => {
    setIsSorting(false)

    const { active, over } = event

    if (
      over &&
      active.id &&
      typeof active.id === 'string' &&
      typeof over.id === 'string' &&
      active.id !== over.id
    ) {
      changeCardOrder(active.id, over.id)
    }
  }

  const onDragStartHandler = (_event: DragStartEvent) => {
    setIsSorting(true)
  }

  // both viewportList and dnd-kit are not prepared for "readonly" lists of items
  const cardOrderW = cardOrder as Writeable<typeof cardOrder>

  return (
    <>
      <div
        className="tray"
        data-open={isTrayOpen}
        style={{
          backdropFilter: lowConstrastBackground
            ? 'brightness(0.9) saturate(2)'
            : undefined,
        }}
      >
        <div
          className="tray-header"
          style={{ color: background.contrast.color }}
        >
          {isTrayOpen ? (
            <TtIconButton
              library="one/webinars"
              name="tray-open"
              label={intl.formatMessage({
                defaultMessage: 'Close webinar tray',
              })}
              onClick={onTrayCloseClickHandler}
            />
          ) : (
            <TtIconButton
              library="one/webinars"
              name="tray-closed"
              label={intl.formatMessage({
                defaultMessage: 'Open webinar tray',
              })}
              onClick={onTrayOpenClickHandler}
            />
          )}
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <div className="tray-header-title">WebinarTray</div>
          {!HasWebinarMixesEnabled && (
            <TrayFloorTrigger disabled={!isTrayOpen} />
          )}
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEndHandler}
          onDragStart={onDragStartHandler}
        >
          <div
            className="tray-list"
            ref={ref}
            style={{
              scrollBehavior:
                isSorting || isSmoothScrollingLocked ? 'auto' : 'smooth',
            }}
          >
            {cardOrder.length === 0 && <GutterMenu isStandalone />}

            <SortableContext
              items={cardOrderW}
              strategy={verticalListSortingStrategy}
            >
              <ViewportList
                ref={cardListRef}
                viewportRef={ref}
                items={cardOrderW}
                axis="y"
                overscan={30}
                scrollThreshold={1}
                withCache
                itemSize={160}
                initialIndex={
                  activeTrayCardIdx !== -1 ? activeTrayCardIdx : undefined
                }
              >
                {(cardId) => <TrayItem key={cardId} id={cardId} />}
              </ViewportList>
            </SortableContext>
          </div>
        </DndContext>
      </div>

      <TtDialog
        open={isUploadingHandout}
        label={intl.formatMessage({ defaultMessage: 'Uploading' })}
        closable={false}
        className="upload-dialog"
      >
        <TtProgressBar indeterminate />
      </TtDialog>
    </>
  )
}

export { Tray }
